import { useProjectCampaigns } from '../../hooks/useProjectCampaigns';
import useCreateProjectCampaign from '../../hooks/useCreateProjectCampaign';
import useProjectId from '../../hooks/useProjectId';
import { creatorbaseQueries } from '@round/api';
import { FormikProvider, useFormik, yupToFormErrors } from 'formik';
import { Modal } from '@round/whitelabel-ui';
import {
    mapCampaignFormValuesToApiData,
    validationSchema as validationSchemaBase,
} from '../../components/CampaignFormBase/CampaignFormBase';
import { Button } from '@round/whitelabel-ui';
import { showNotification } from 'helpers';
import { omit } from 'lodash';
import TiktokCampaignForm, {
    TiktokCampaignFormValues,
    mapTiktokCampaignApiErrorsToFormikErrors,
} from '../components/TiktokCampaignForm';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import useNonNullContext from 'Hooks/useNonNullContext';
import styles from './CreateTiktokCampaignModal.module.css';
import useProjectDetails from 'Modules/Plans/Project/ProjectDetails/useProjectDetails';
import { useClient, useUser } from '@round/creatorbase-helpers/api-hooks';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import * as yup from 'yup';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { useCurrentCreatorbaseUser } from 'Modules/Auth/hooks/useCurrentCreatorbaseUser';
import { mapUserToOption } from 'Modules/Plans/User/hooks/useUsersSelect';
type Props = {
    isOpen: boolean;
    closeModal: () => void;
};

const CreateTiktokCampaignModal = ({ isOpen, closeModal }: Props) => {
    const projectId = useProjectId();
    const { create: createCampaign } = useCreateProjectCampaign();
    const isCurrentUserCreatorbaseAdmin = useCheckUserGroupsAccess(['creatorbase_admin'], 'all');

    const { creatorbaseUserId } = useCurrentCreatorbaseUser();
    const { data: currentCreatorbaseUser } = useUser(creatorbaseUserId, { enabled: !isCurrentUserCreatorbaseAdmin });

    const { currencies } = useNonNullContext(OptionsContext);
    const { isSuccess: areCampaignsLoaded } = useProjectCampaigns('tiktok');

    const { team, project } = useProjectDetails();
    const { client } = useClient(team?.client_id);
    const isCampaignPaymentRequestsEnabled = useFeatureFlags().isEnabled('rou_539_campaign_payment_requests');
    const arePaymentsEnabled = isCampaignPaymentRequestsEnabled && !!client?.is_payment_enabled;
    const projectCurrency = currencies.find((curr) => curr.id === project?.currency_id) || null;

    const formik = useFormik<TiktokCampaignFormValues>({
        initialValues: {
            audio_identifier: '',
            cost: '',
            currency: projectCurrency ? { value: projectCurrency.id, label: projectCurrency.name } : null,
            is_post_cost_editable: true,
            name: '',
            team_members:
                currentCreatorbaseUser && !isCurrentUserCreatorbaseAdmin
                    ? [mapUserToOption(currentCreatorbaseUser)]
                    : [],
        },
        onSubmit: (values, helpers) => {
            if (values.is_post_cost_editable === null) {
                helpers.setSubmitting(false);
                return;
            }
            if (!projectId) {
                showNotification('Seems like project page was not initialized correctly', 'error');
                helpers.setSubmitting(false);
                return;
            }

            createCampaign(
                {
                    project_id: projectId,
                    ...mapCampaignFormValuesToApiData(values, arePaymentsEnabled),
                    platform: 'tiktok',
                    tiktok_details: { audio_identifier: values.audio_identifier },
                },
                {
                    onSuccess: () => {
                        showNotification('Campaign created', 'info');
                        helpers.resetForm();
                        closeModal();
                    },
                    onError: (error) => {
                        if (
                            creatorbaseQueries.campaigns.utils.create.isExpectedErrorResponse(error) &&
                            error.response.status === 400
                        ) {
                            helpers.setErrors(mapTiktokCampaignApiErrorsToFormikErrors(error.response.data));
                            return;
                        }

                        showNotification('Could not create campaign', 'error');
                    },
                    onSettled: () => {
                        helpers.setSubmitting(false);
                    },
                }
            );
        },
        // validationSchema param threw an error on validation
        validate: async (values) => {
            const validationSchema = validationSchemaBase.shape({
                audio_identifier: yup.string().optional(),
            });

            try {
                const transformedValues = values.is_post_cost_editable === false ? values : omit(values, 'cost');
                await validationSchema.validate(transformedValues, { abortEarly: false });
            } catch (e) {
                return yupToFormErrors(e);
            }
        },
        enableReinitialize: true,
    });

    const handleClose = () => {
        formik.resetForm();
        closeModal();
    };

    // Shouldn't be able to create campaign, when we haven't checked for currencies of other campaigns
    const isSubmitDisabled = !formik.dirty || !formik.isValid || !areCampaignsLoaded;
    return (
        <Modal
            className={styles.modal}
            closeOnOverlayClick
            isOpen={isOpen}
            onClose={handleClose}
            ariaLabel="Create tiktok campaign modal"
        >
            <Modal.Header>
                <Modal.Title>Create campaign</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormikProvider value={formik}>
                    <TiktokCampaignForm readonlyFields={projectCurrency ? ['currency'] : undefined} teamId={team?.id} />
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    disabled={isSubmitDisabled}
                    isLoading={formik.isSubmitting}
                    onClick={formik.submitForm}
                >
                    Create campaign
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CreateTiktokCampaignModal;
